import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiList } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BreadcrumbBack from '../components/BreadcrumbBack';
import RendersPanel, { DerivedShorts, RenderJob, RenderOutput, RenderEdit } from '../components/clip-editor/RendersPanel';
import { can, ProjectAccess } from '../hooks/useProjectAccess';
import GenerateShortsModal, { GenerateShortsTarget } from '../components/clip-editor/GenerateShortsModal';
import { ItemKind } from '../components/project/ProjectItemList';

/** Y-Ray items have their own page and route (pages/YRayItem.tsx); this viewer handles the other three. */
type ViewerKind = Exclude<ItemKind, 'y-ray'>;

const ListIcon = FiList as unknown as React.FC<{ 'aria-hidden'?: boolean }>;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const KINDS: ViewerKind[] = ['story', 'caption', 'shorts'];
const isKind = (v: string | undefined): v is ViewerKind => !!v && (KINDS as string[]).includes(v);

const KIND_LABEL: Record<ViewerKind, string> = { story: 'Story', caption: 'Caption', shorts: 'Shorts' };
const KIND_GLYPH: Record<ViewerKind, string> = { story: '📖', caption: '💬', shorts: '🎬' };

/**
 * Where each kind's own record lives. Reading the one item is what gives the page its name and
 * lets a bad id 404 — asking the project for every item it holds ran three subquery-heavy
 * branches to end up with a single string.
 */
const detailUrlFor = (kind: ViewerKind, projectId: string, id: string): string => {
  if (kind === 'story') return `${API_BASE}/api/image-projects/${id}`;
  if (kind === 'caption') return `${API_BASE}/api/caption-workspaces/${id}`;
  return `${API_BASE}/api/projects/${projectId}/videos/${id}`;
};

/** Where each kind's render history is listed. All three return the same row shape. */
const jobsUrlFor = (kind: ViewerKind, id: string): string => {
  if (kind === 'story') return `${API_BASE}/api/image-projects/${id}/jobs`;
  if (kind === 'caption') return `${API_BASE}/api/caption-workspaces/${id}/jobs`;
  return `${API_BASE}/api/videos/${id}/jobs`;
};

/** What a render is called in the list, per kind. */
const labelForKind = (kind: ViewerKind) => (job: RenderJob): string => {
  if (job.status === 'failed') return 'Render failed';
  if (job.isSource) return 'Uploaded video';
  if (kind === 'story') return 'Story render';
  if (kind === 'caption') return job.kineticCaption?.preset
    ? String(job.kineticCaption.preset).split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : 'Caption render';
  return 'AI Shorts';
};


/**
 * The original upload as a row of its own, so every Shorts and Caption item can play what it was
 * made from. A story is built from many assets rather than one video, so it has none. Both detail
 * responses carry a signed `sourceUrl`; a caption's also reports the file's duration and size
 * under `source*` names, since its own `name` and `createdAt` belong to the workspace.
 */
const buildSourceJob = (kind: ViewerKind, itemId: string, detail: any): RenderJob | null => {
  if (kind === 'story' || !detail?.sourceUrl) return null;
  const durationRaw = kind === 'caption' ? detail.sourceDurationSeconds : detail.duration;
  const sizeRaw = kind === 'caption' ? detail.sourceFileSize : detail.file_size;
  const duration = Number(durationRaw);
  const size = Number(sizeRaw);
  return {
    id: `source-${itemId}`,
    status: 'completed',
    stage: 'completed',
    progress: 100,
    kineticCaption: null,
    isSource: true,
    outputUrl: detail.sourceUrl,
    outputs: [{
      shortId: null,
      index: 0,
      title: detail.name || detail.file_name || null,
      url: detail.sourceUrl,
      durationSeconds: Number.isFinite(duration) && duration > 0 ? duration : null,
      score: null,
      startTime: null,
      endTime: null,
      segments: null,
      fileSize: Number.isFinite(size) && size > 0 ? size : null,
      edits: [],
    }],
    details: [],
    error: null,
    createdAt: detail.created_at || detail.createdAt || new Date().toISOString(),
  };
};

const clock = (secs: number | null): string => {
  if (secs === null || secs === undefined || secs < 0) return '';
  const m = Math.floor(secs / 60);
  const rest = Math.floor(secs % 60);
  return `${m}:${rest.toString().padStart(2, '0')}`;
};

const bytes = (n: number | null): string => {
  if (!n || n <= 0) return '';
  const mb = n / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
};

/**
 * The pipeline has written scores on two scales over its life — 0-10 early on, 0-100 since. One
 * number is meaningless without saying which, so both are pulled onto 0-100 before display.
 */
const confidence = (score: number | null): number | null => {
  const n = Number(score);
  if (score === null || score === undefined || !Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, Math.round(n <= 10 ? n * 10 : n)));
};

/** Tags are stored as JSONB and have arrived as both an array and a comma string. */
const tagList = (tags: unknown): string[] => {
  const raw = Array.isArray(tags)
    ? tags
    : typeof tags === 'string' ? tags.split(',') : [];
  return raw
    .map(t => String(t).trim().replace(/^#/, ''))
    .filter(Boolean);
};

const stamp = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

/** What the player is on: a clip, and which of its versions — null being the original cut. */
/** What an item with no render yet is waiting for, and what will appear here once it has one. */
const EMPTY_COPY: Record<ViewerKind, { waiting: string; then: string; cta: string }> = {
  caption: {
    waiting: 'No captioned video yet.',
    then: 'Pick a kinetic caption style and generate one. Once the captions are burnt in, the video plays here with its clip details and the settings it was rendered with.',
    cta: 'Open Caption editor',
  },
  story: {
    waiting: 'No story render yet.',
    then: 'Build the story and generate it. Once rendered, the video plays here with its details and the settings it was made with.',
    cta: 'Open Story editor',
  },
  shorts: {
    waiting: 'No shorts yet.',
    then: 'Process this video to cut shorts from it. Each one plays here, with the render that produced it and any clip edits you make.',
    cta: 'Open video',
  },
};

interface Selection { job: RenderJob; output: RenderOutput; edit: RenderEdit | null }

/**
 * What the author wrote on a clip, as opposed to what the render measured. The renders endpoint
 * does not carry it, but the item's own detail response already does — this is that, indexed by
 * the clip id the render outputs are keyed on.
 */
interface ClipInfo { title: string | null; caption: string | null; tags: string[] }

const urlOf = (sel: Selection): string | null => sel.edit ? sel.edit.url : sel.output.url;

/**
 * The read-only landing page for anything a project holds — a Story, a Caption workspace or an
 * uploaded video's Shorts.
 *
 * The right panel is the item's render history: every run it ever made, newest first, with the
 * videos that run produced nested under it. A pipeline run cut several clips and lists them all;
 * a story or caption render made one and lists it flat. Nothing here edits — the editors keep
 * their own routes, and Edit is a way into them, resuming whatever settings the owner holds now.
 */
const ItemViewer: React.FC = () => {
  const { projectId, kind, itemId } = useParams<{ projectId: string; kind: ViewerKind; itemId: string }>();
  const navigate = useNavigate();
  const [shortsTarget, setShortsTarget] = useState<GenerateShortsTarget | null>(null);

  const [projectName, setProjectName] = useState<string>('');
  // Shared (read-only) projects: hide editors/downloads the caller's role lacks. The API enforces it too.
  const [access, setAccess] = useState<ProjectAccess | null>(null);
  const [itemName, setItemName] = useState<string>('');
  const [clipInfo, setClipInfo] = useState<Record<string, ClipInfo>>({});
  const [sourceJob, setSourceJob] = useState<RenderJob | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Selection | null>(null);
  const [videoCount, setVideoCount] = useState(0);
  // Rows, not videos: a render that failed or is still running is still a reason not to show
  // the "nothing here yet" state.
  const [renderCount, setRenderCount] = useState(0);
  // Bumped on arrival so a just-queued export appears immediately rather than up to a poll later.
  const [refreshSignal] = useState(() => Date.now());
  // Auto-selection is a first-load convenience, not a rule: once the author has picked a video,
  // a poll returning fresh jobs must not yank the player back to the newest render.
  // Below 900px the renders list is a left-hand overlay drawer opened from the breadcrumb.
  const [rendersOpen, setRendersOpen] = useState(false);
  useEffect(() => {
    if (!rendersOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setRendersOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [rendersOpen]);

  const pickedRef = useRef(false);
  // Auto-selection is not a request to play. Only a render the author clicked starts playing;
  // a freshly opened page shows its first render paused on frame one.
  const userPickedRef = useRef(false);
  const jobsRef = useRef<RenderJob[]>([]);
  // Read when opening the editor, which must not re-create its callback every time the name loads.
  const itemNameRef = useRef('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  /** The file actually playing: the chosen edit, or the clip's own cut when none is chosen. */
  const selectedUrl = selected ? urlOf(selected) : null;

  // Everything below belongs to one item. The route can swap the item under a mounted component,
  // which without this leaves the previous one's video playing over the new one's page.
  useEffect(() => {
    pickedRef.current = false;
    userPickedRef.current = false;
    setSelected(null);
    setIsPlaying(false);
    setVideoCount(0);
    setRenderCount(0);
    setItemName('');
    setClipInfo({});
    setSourceJob(null);
    setNotFound(false);
    setLoading(true);
  }, [kind, itemId]);

  useEffect(() => {
    if (!isKind(kind) || !itemId || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const [projRes, itemRes] = await Promise.all([
          fetch(`${API_BASE}/api/projects/${projectId}`, { credentials: 'include' }),
          fetch(detailUrlFor(kind, projectId, itemId), { credentials: 'include' }),
        ]);
        if (cancelled) return;
        if (projRes.ok) {
          const proj = await projRes.json();
          setProjectName(proj?.name || '');
          setAccess(proj?.access ?? null);
        }
        if (itemRes.ok) {
          const detail = await itemRes.json();
          const name = detail?.name || detail?.file_name || '';
          setItemName(name);
          itemNameRef.current = name;
          // Only a video detail carries clips; a story or caption response simply has none.
          const clips: Array<{ id: string; title?: string | null; caption?: string | null; tags?: unknown }> =
            Array.isArray(detail?.shorts) ? detail.shorts : [];
          setClipInfo(Object.fromEntries(clips.map(c => [
            String(c.id),
            { title: c.title ?? null, caption: c.caption ?? null, tags: tagList(c.tags) },
          ])));
          setSourceJob(buildSourceJob(kind, itemId, detail));
        } else {
          setNotFound(true);
        }
      } catch { if (!cancelled) setNotFound(true); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [projectId, kind, itemId]);

  const handleJobs = useCallback((jobs: RenderJob[]) => {
    // Held for the error path below. A poll must never touch `selected`: every fetch re-presigns
    // the keys, and swapping a live `src` for a freshly signed one reloads the video and restarts
    // playback — once every poll, for as long as the page is open.
    jobsRef.current = jobs;
    setVideoCount(jobs.reduce((n, j) => n + (j.outputs || []).length, 0));
    setRenderCount(jobs.length);

    if (pickedRef.current) return;
    const job = jobs.find(j => (j.outputs || []).length > 0);
    if (job) {
      pickedRef.current = true;
      setSelected({ job, output: job.outputs[0], edit: null });
    }
  }, []);

  /**
   * A presigned link lasts an hour; a page can be left open longer. Waiting for the video to
   * actually fail costs nothing and reloads it once, where refreshing on a timer reloaded it
   * constantly — and by then the poll has a live URL for the same output ready to swap in.
   */
  const handleVideoError = useCallback(() => {
    // The source is not in the renders poll, so its link is re-read from the item itself. A link
    // that comes back unchanged is not retried, which keeps a genuinely missing file from looping.
    if (selected?.job.isSource && isKind(kind) && itemId && projectId) {
      const staleUrl = selected.output.url;
      void (async () => {
        try {
          const res = await fetch(detailUrlFor(kind, projectId, itemId), { credentials: 'include' });
          if (!res.ok) return;
          const fresh = buildSourceJob(kind, itemId, await res.json());
          if (!fresh || fresh.outputs[0].url === staleUrl) return;
          setSourceJob(fresh);
          setSelected(prev => (prev?.job.isSource ? { job: fresh, output: fresh.outputs[0], edit: null } : prev));
        } catch { /* the controls stay; nothing to do */ }
      })();
      return;
    }
    setSelected(prev => {
      if (!prev) return prev;
      const job = jobsRef.current.find(j => j.id === prev.job.id);
      const output = job?.outputs?.find(o => o.index === prev.output.index);
      if (!output) return prev;
      const edit = prev.edit ? (output.edits || []).find(e => e.id === prev.edit!.id) || null : null;
      const next: Selection = { job: job!, output, edit };
      const url = urlOf(next);
      return url && url !== urlOf(prev) ? next : prev;
    });
  }, [selected, kind, itemId, projectId]);

  /**
   * Autostart, honestly.
   *
   * Only for a render the author picked. Opening the page auto-selects the newest render so there
   * is something on screen, and that selection stays paused — nothing plays until it is asked to.
   * A click has interaction behind it, so the play() below is allowed to keep its audio.
   */
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !selected?.output.url || !userPickedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        await el.play();
      } catch {
        if (cancelled || !videoRef.current) return;
        videoRef.current.muted = true;
        try { await videoRef.current.play(); } catch { /* controls are right there */ }
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.job.id, selected?.output.index, selected?.edit?.id, selectedUrl]);

  /** A row's transport control: pause what is playing, or switch to and play anything else. */
  const handleTogglePlay = useCallback((job: RenderJob, output: RenderOutput, edit?: RenderEdit | null) => {
    const next: Selection = { job, output, edit: edit ?? null };
    setSelected(prev => {
      const isCurrent = prev
        && prev.job.id === job.id
        && prev.output.index === output.index
        && (prev.edit?.id ?? null) === (next.edit?.id ?? null);
      if (isCurrent) {
        const el = videoRef.current;
        if (el) { if (el.paused) void el.play().catch(() => {}); else el.pause(); }
        return prev;
      }
      if (!urlOf(next)) return prev;
      pickedRef.current = true;
      userPickedRef.current = true;
      return next;
    });
  }, []);

  const handleView = useCallback((job: RenderJob, output?: RenderOutput, edit?: RenderEdit | null) => {
    const picked = output || job.outputs?.[0];
    if (!picked) return;
    const next: Selection = { job, output: picked, edit: edit ?? null };
    if (!urlOf(next)) return;
    pickedRef.current = true;
    userPickedRef.current = true;
    setSelected(next);
  }, []);

  /**
   * Editing a clip opens the clip editor on that clip, told how to get back here. The editor's
   * Done button saves, runs the export, and returns — the new version then shows up under its
   * clip as a queued row and fills in as the job runs.
   */
  const openClipEditor = useCallback((shortId: string) => {
    const back = `${window.location.pathname}${window.location.search}`;
    const label = encodeURIComponent(itemNameRef.current || KIND_LABEL[kind as ViewerKind] || 'Renders');
    navigate(`/clips/${shortId}?returnTo=${encodeURIComponent(back)}&exportOnDone=1&returnLabel=${label}`);
  }, [navigate, kind]);

  const handleEditOutput = useCallback((_job: RenderJob, output: RenderOutput) => {
    if (output.shortId) openClipEditor(output.shortId);
  }, [openClipEditor]);

  // "Generate Shorts" only makes sense on a Caption's renders — the endpoint is scoped to a
  // caption workspace, and this page's item id is that workspace.
  const handleGenerateShorts = useCallback((job: RenderJob, output?: RenderOutput, edit?: RenderEdit | null) => {
    const base = itemNameRef.current || output?.title || 'Caption';
    setShortsTarget(
      edit
        ? { jobId: edit.id, defaultName: `${base} (edited) — Shorts`, timelinePreserved: false }
        : { jobId: job.id, defaultName: `${base} — Shorts`, timelinePreserved: true }
    );
  }, []);

  const openDerivedShorts = useCallback((derived: DerivedShorts) => {
    navigate(`/projects/${derived.projectId || projectId}/item/shorts/${derived.videoId}`);
  }, [navigate, projectId]);

  /** Editing the item reopens its own editor, which loads whatever settings it holds now. */
  const openOwnerEditor = useCallback(() => {
    if (kind === 'story') navigate(`/projects/${projectId}/image-video/${itemId}`);
    else if (kind === 'caption') navigate(`/projects/${projectId}/kinetic-caption/${itemId}`);
    else navigate(`/projects/${projectId}/video/${itemId}`);
  }, [navigate, kind, projectId, itemId]);

  const canEditItem = can(access, kind === 'story' ? 'can_edit_story' : kind === 'caption' ? 'can_edit_caption' : 'can_edit_clip');
  const canClip = can(access, 'can_edit_clip');
  const canPublish = can(access, 'can_publish');
  const canDownload = can(access, 'can_download');

  const jobsUrl = useMemo(
    () => (isKind(kind) && itemId ? jobsUrlFor(kind, itemId) : ''),
    [kind, itemId]
  );

  const title = itemName || (loading ? '' : 'Untitled');

  const metaRows = useMemo<Array<[string, string]>>(() => {
    if (!selected) return [];
    const { job, output } = selected;
    const rows: Array<[string, string]> = [];

    rows.push(['Render', labelForKind(kind as ViewerKind)(job)]);
    if (output.shortId) {
      rows.push(['Clip', `#${output.index}`]);
      const edits = output.edits || [];
      if (edits.length) {
        const i = selected.edit ? edits.findIndex(e => e.id === selected.edit!.id) : -1;
        rows.push(['Version', i >= 0 ? `Edit v${edits.length - i + 1}` : 'Original']);
      }
    }
    const dur = clock(output.durationSeconds);
    if (dur) rows.push(['Duration', dur]);
    if (output.segments && output.segments.length > 0) {
      rows.push([
        'Summary of',
        `${output.segments.length} moments — ${output.segments
          .map(s => `${clock(s.start)}–${clock(s.end)}`)
          .join(', ')}`,
      ]);
    } else if (output.startTime !== null && output.endTime !== null) {
      rows.push(['Source range', `${clock(output.startTime)} – ${clock(output.endTime)}`]);
    }
    const size = bytes(output.fileSize);
    if (size) rows.push(['Size', size]);
    rows.push([job.isSource ? 'Uploaded' : 'Rendered', stamp(job.createdAt)]);
    // Labels are the render keys, so a repeated one would drop a row rather than show both.
    return Array.from(new Map(rows).entries());
  }, [selected, kind]);

  /** The clip the player is on, when it is on one: its title, description and tags. */
  const clip: ClipInfo | null = selected?.output.shortId
    ? clipInfo[selected.output.shortId] ?? null
    : null;

  /** The clip's title as the render reported it, falling back to the item's own record. */
  const clipTitle = selected?.output.title || clip?.title || '';

  const clipScore = confidence(selected?.output.score ?? null);

  if (!isKind(kind) || !itemId) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="iv-notfound">
            <p>That link doesn't point at anything we can show.</p>
            <Link className="btn-sm btn-sm-accent" to={`/projects/${projectId || ''}`}>Back to project</Link>
          </div>
        </main>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="iv-notfound">
            <p>This {KIND_LABEL[kind].toLowerCase()} no longer exists, or isn't yours to open.</p>
            <Link className="btn-sm btn-sm-accent" to={`/projects/${projectId}`}>Back to project</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <div className="iv-crumb-row">
          <div className="breadcrumb">
            <BreadcrumbBack to={`/projects/${projectId}`} />
            <Link to="/projects">Projects</Link>
            <span className="breadcrumb-sep">&#8250;</span>
            <Link to={`/projects/${projectId}`}>{projectName || 'Project'}</Link>
            <span className="breadcrumb-sep">&#8250;</span>
            <span>{title}</span>
          </div>

          {/* The badge says what this is; editing it is a quiet affordance beside it rather
              than a button competing with the page. */}
          <div className="iv-crumb-actions">
            <span className={`pi-badge pi-badge--${kind}`}>{KIND_GLYPH[kind]} {KIND_LABEL[kind]}</span>
            {canEditItem && (
              <button
                className="iv-edit-icon"
                onClick={openOwnerEditor}
                title={`Edit ${KIND_LABEL[kind]}`}
                aria-label={`Edit ${KIND_LABEL[kind]}`}
              >
                ✎
              </button>
            )}
            {/* Below 900px the renders live in a left-hand overlay; this opens it. */}
            <button
              className="iv-edit-icon iv-renders-jump"
              onClick={() => setRendersOpen(true)}
              title="Show renders"
              aria-label="Show renders"
              aria-expanded={rendersOpen}
            >
              <ListIcon aria-hidden={true} />
            </button>
          </div>
        </div>

        <div className="iv-body">

          <section className="iv-player">
            {selectedUrl ? (
              <video
                key={`${selected!.job.id}:${selected!.output.index}:${selected!.edit?.id ?? 'original'}`}
                ref={videoRef}
                className="iv-player-video"
                src={selectedUrl}
                onError={handleVideoError}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                controls
              />
            ) : (
              <div className="iv-player-empty">
                {loading ? (
                  'Loading…'
                ) : renderCount > 0 ? (
                  'Pick a render on the right to play it.'
                ) : (
                  /* Nothing has been rendered, so there is nothing to pick. Say what is missing,
                     what will show up here once it exists, and offer the way to make it. */
                  <div className="iv-empty-state">
                    <p className="iv-empty-title">{EMPTY_COPY[kind].waiting}</p>
                    <p className="iv-empty-body">{EMPTY_COPY[kind].then}</p>
                    {canEditItem && (
                      <button className="btn-sm btn-sm-accent" onClick={openOwnerEditor}>
                        ✎ {EMPTY_COPY[kind].cta}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* The item names itself under its video, on the same row as what you can do
                with the file that is playing. */}
            <div className="iv-player-bar">
              <h1 className="iv-title">{title}</h1>
              {selectedUrl && selected && (
                <div className="iv-player-bar-actions">
                  {canClip && selected.output.shortId && (
                    <button className="btn-sm" onClick={() => openClipEditor(selected.output.shortId!)}>
                      ✎ Edit clip
                    </button>
                  )}
                  {canPublish && selected.output.shortId && projectId && itemId && (
                    <Link
                      className="btn-sm"
                      to={kind === 'shorts'
                        ? `/posts/compose?projectId=${encodeURIComponent(projectId)}&videoId=${encodeURIComponent(itemId)}&shortId=${encodeURIComponent(selected.output.shortId)}`
                        : `/posts/compose?projectId=${encodeURIComponent(projectId)}&kind=${kind}&itemId=${encodeURIComponent(itemId)}&shortId=${encodeURIComponent(selected.output.shortId)}`}
                    >
                      📤 Publish
                    </Link>
                  )}
                  {canDownload && <a className="btn-sm" href={selectedUrl} target="_blank" rel="noreferrer">⬇ Download</a>}
                </div>
              )}
            </div>

            {selectedUrl && selected && (
              <>

                {/* What is being described, and how sure the pipeline was of it. The pill is
                    the one number worth reading at a glance, so it is not buried in the strip. */}
                <div className="iv-details-head">
                  <span className="iv-details-title">
                    {selected.output.shortId ? 'Clip details' : 'Render details'}
                  </span>
                  {clipScore !== null && (
                    <span className="iv-score-pill">Confidence Score {clipScore}/100</span>
                  )}
                </div>

                {/* The text an author reads rather than scans — a title, the clip's description
                    and its tags. Full-width rows, because these are the only values here that
                    run past a line and must be allowed to wrap instead of being clipped. */}
                {Boolean(clipTitle || clip?.caption || clip?.tags.length) && (
                  <div className="iv-clipinfo">
                    {clipTitle && (
                      <div className="iv-clipinfo-row">
                        <span className="iv-clipinfo-label">Title</span>
                        <p className="iv-clipinfo-value iv-clipinfo-title">{clipTitle}</p>
                      </div>
                    )}
                    {clip?.caption && (
                      <div className="iv-clipinfo-row">
                        <span className="iv-clipinfo-label">Description</span>
                        <p className="iv-clipinfo-value">{clip.caption}</p>
                      </div>
                    )}
                    {!!clip?.tags.length && (
                      <div className="iv-clipinfo-row">
                        <span className="iv-clipinfo-label">Tags</span>
                        <span className="iv-clipinfo-value iv-tags">
                          {clip.tags.map(t => <span className="iv-tag" key={t}>#{t}</span>)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* The measured facts: one line each, so a strip of equal cells is the right
                    shape for them where it was the wrong one for the text above. */}
                <dl className="iv-meta">
                  {metaRows.map(([label, value]) => (
                    <div className="iv-meta-cell" key={label}>
                      <dt className="iv-meta-label">{label}</dt>
                      <dd className="iv-meta-value">{value}</dd>
                    </div>
                  ))}
                </dl>

                {/* How this file came to exist, as opposed to what it is: the settings the job
                    was created with, reported by the API rather than read off the stored
                    payload, which holds credentials the browser must never see. */}
                {selected.job.details?.length > 0 && (
                  <div className="iv-jobdetails">
                    <div className="iv-jobdetails-head">Render job</div>
                    <dl className="iv-meta iv-meta--nested">
                      {selected.job.details.map(({ label, value }) => (
                        <div className="iv-meta-cell" key={label}>
                          <dt className="iv-meta-label">{label}</dt>
                          <dd className="iv-meta-value" title={value}>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </>
            )}
          </section>

          <div className={`iv-renders-backdrop${rendersOpen ? ' is-open' : ''}`} onClick={() => setRendersOpen(false)} aria-hidden="true" />
          <aside className={`iv-renders${rendersOpen ? ' iv-renders--open' : ''}`}>
            <div className="iv-panel-head">
              <span className="iv-panel-title">Renders</span>
              {videoCount > 0 && <span className="iv-panel-count">{videoCount}</span>}
              <button
                className="iv-renders-close"
                onClick={() => setRendersOpen(false)}
                aria-label="Close renders"
              >
                ✕
              </button>
            </div>
            {/* The list scrolls inside the column; the heading stays put above it, and the
                column itself sticks so the player is never scrolled out to reach a render. */}
            <div className="iv-renders-scroll">
            <RendersPanel
              jobsUrl={jobsUrl}
              labelFor={labelForKind(kind)}
              emptyText={
                kind === 'shorts'
                  ? 'No renders yet. Process this video to generate shorts.'
                  : `No renders yet. Open the ${KIND_LABEL[kind].toLowerCase()} editor to generate one.`
              }
              onJobsChange={handleJobs}
              onView={handleView}
              onEdit={canClip ? handleEditOutput : undefined}
              onGenerateShorts={kind === 'caption' && canEditItem ? handleGenerateShorts : undefined}
              canDownload={canDownload}
              onOpenDerivedShorts={openDerivedShorts}
              selectedJobId={selected?.job.id || null}
              selectedOutputIndex={selected?.output.index ?? null}
              selectedEditId={selected?.edit?.id ?? null}
              isPlaying={isPlaying}
              onTogglePlay={handleTogglePlay}
              refreshSignal={refreshSignal}
              sourceJob={sourceJob}
            />
            </div>
          </aside>
        </div>

        {kind === 'caption' && projectId && itemId && (
          <GenerateShortsModal
            open={!!shortsTarget}
            target={shortsTarget}
            projectId={projectId}
            workspaceId={itemId}
            onClose={() => setShortsTarget(null)}
            onGenerated={({ videoId }) => {
              setShortsTarget(null);
              // The clips are what was asked for, so land on the item that will hold them.
              navigate(`/projects/${projectId}/item/shorts/${videoId}`);
            }}
          />
        )}
      </main>
    </div>
  );
};

export default ItemViewer;
